<template>
  <div class="frisbuy-select" :style="customStyle">
    <div
      v-if="isSelectMenuOpen"
      class="frisbuy-select__backdrop"
      @click="closeSelectMenu"
    ></div>

    <button
      :disabled="props.disabled"
      class="frisbuy-select__select-button"
      :class="{ 'frisbuy-select__select-button--is-open': isSelectMenuOpen }"
      @click="selectMenuClickHandler"
    >
      <span value="">{{ selectedOption.text }} </span>

      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
          d="M15.0848 8.41976C15.4102 8.09435 15.4101 7.56668 15.0848 7.24124C14.7594 6.91582 14.2317 6.91581 13.9063 7.24124L9.99846 11.1491L6.09061 7.24125L6.02732 7.1837C5.70001 6.91691 5.21714 6.93621 4.9121 7.24125C4.60717 7.54629 4.58778 8.02919 4.85456 8.35646L4.9121 8.41976L9.23254 12.7402C9.65554 13.1631 10.3413 13.163 10.7644 12.7402L15.0848 8.41976Z"
          fill="currentColor"
        />
      </svg>
    </button>

    <div
      class="frisbuy-select__options"
      :class="{ '--is-closed': !isSelectMenuOpen }"
      ref="selectOptions"
      v-click-outside="closeSelectMenu"
      tabindex="-1"
    >
      <button
        class="frisbuy-select__mobile-close"
        type="button"
        @click="closeSelectMenu"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M17.6077 6.29437C17.2138 5.90724 16.5807 5.91272 16.1936 6.3066L11.9878 10.5857L7.70878 6.38003C7.31489 5.9929 6.68175 5.99838 6.29462 6.39227C5.90748 6.78615 5.91296 7.4193 6.30685 7.80643L10.5859 12.0121L6.38006 16.2914C5.99293 16.6852 5.9984 17.3184 6.39229 17.7055C6.78618 18.0926 7.41932 18.0872 7.80645 17.6933L12.0123 13.414L16.2916 17.6199C16.6855 18.0071 17.3186 18.0016 17.7058 17.6077C18.0929 17.2138 18.0874 16.5807 17.6935 16.1935L13.4142 11.9876L17.6199 7.70853C18.0071 7.31465 18.0016 6.6815 17.6077 6.29437Z"
            fill="currentColor"
          />
        </svg>
      </button>

      <span class="frisbuy-select__popper--arrow"></span>

      <div
        v-for="option in props.options"
        :key="`${option.value}-${option.sort || ''}`"
        class="frisbuy-select__option"
        :class="{
          'frisbuy-select__option--disabled': props.disabled,
          'frisbuy-select__option--selected':
            selectedOption?.value === option.value &&
            selectedOption?.sort === option.sort,
        }"
        @click="() => selectOption(option)"
      >
        <span class="frisbuy-select__option-text">
          {{ option.text }}
        </span>
        <svg
          v-if="
            selectedOption?.value === option.value &&
            selectedOption?.sort === option.sort
          "
          class="frisbuy-select__option-check"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M16.4863 10.7071C16.8768 10.3166 16.8768 9.68342 16.4863 9.29289C16.0957 8.90237 15.4626 8.90237 15.072 9.29289L10.8289 13.536L8.70711 11.4143C8.31658 11.0237 7.68342 11.0237 7.29289 11.4143C6.90237 11.8048 6.90237 12.4379 7.29289 12.8285L10.1213 15.6569C10.3297 15.8653 10.6071 15.9624 10.8799 15.9485C11.1187 15.9365 11.3541 15.8393 11.5365 15.6569L16.4863 10.7071Z"
            fill="currentColor"
          />
        </svg>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
$select-menu-showing-animation-time: 0.1s;

.frisbuy-select {
  &,
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  position: relative;

  width: 100%;
  height: 100%;

  font-size: 16px;

  .frisbuy-select__backdrop {
    display: none;

    @media only screen and (max-width: 1024px) {
      display: block;
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.55);
      z-index: 1000;
    }
  }

  .frisbuy-select__select-button {
    display: flex;
    gap: 4px;
    position: relative;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: center;

    background-color: #ffffff;

    border: 1px solid rgba(229, 229, 229, 1);
    border-radius: 999px;

    width: 100%;
    height: 100%;

    padding: 12px 16px;
    cursor: pointer;
    transition: border-color 0.3s ease, color 0.3s ease;

    span {
      color: rgba(33, 33, 33, 1);
      font-size: 14px;
      line-height: 20px;
      font-weight: 400;
      transition: color 0.3s ease;
    }

    svg {
      width: 20px;
      height: 20px;
      flex-shrink: 0;

      transition: transform $select-menu-showing-animation-time linear;

      path {
        fill: rgba(33, 33, 33, 1);
        transition: fill 0.3s ease;
      }
    }

    &:hover:not(:disabled):not(.frisbuy-select__select-button--is-open) {
      border-color: #00b9bf;

      span {
        color: #00b9bf;
      }

      svg path {
        fill: #00b9bf;
      }
    }
  }

  .frisbuy-select__select-button--is-open {
    border-color: #00b9bf;

    span {
      color: #00b9bf;
    }

    svg path {
      fill: #00b9bf;
    }

    svg {
      transform: rotateZ(180deg);
    }
  }

  .frisbuy-select__select-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .frisbuy-select__options {
    display: flex;
    position: absolute;
    flex-direction: column;
    flex-wrap: nowrap;

    top: 100%;
    left: 50%;
    transform: translateX(-50%);

    min-width: 253px;
    width: 253px;
    max-width: 253px;

    background-color: #ffffff;

    border: 1px solid #f0f0f0;
    border-radius: 18px;
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.08);

    transition-property: overflow, border-width, padding;
    transition-duration: $select-menu-showing-animation-time;
    transition-timing-function: linear;

    padding: 8px 0;
    margin: 0;

    z-index: 3;

    @media only screen and (max-width: 1024px) {
      position: fixed;
      left: 0;
      right: 0;
      top: auto;
      bottom: 0;
      transform: none;

      width: 100%;
      min-width: 100%;
      max-width: 100%;
      padding: 40px 0;

      border-radius: 12px 12px 0 0;
      box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.12);
      z-index: 1001;
    }

    &.--is-closed {
      visibility: hidden;
      pointer-events: none;
      overflow: hidden;
      margin-top: 0;

      border-width: 0;

      padding: 0;

      .frisbuy-select__popper--arrow {
        opacity: 0;
      }

      .frisbuy-select__option {
        height: 0;
        flex: 1 1 0;

        opacity: 0;
      }
    }

    .frisbuy-select__popper--arrow {
      display: none;
    }

    .frisbuy-select__mobile-close {
      display: none;

      @media only screen and (max-width: 1024px) {
        display: flex;
        position: absolute;
        top: 0;
        right: 0;
        width: 40px;
        height: 40px;
        border: 0;
        background: transparent;
        color: var(--frisbuy-color-primary-3, #999999);
        align-items: center;
        justify-content: center;
        cursor: pointer;
      }
    }

    .frisbuy-select__option {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex: 1 1 34px;

      // min-height: 40px;

      background-color: inherit;

      transition-property: background-color, height, flex, opacity;
      transition-duration: $select-menu-showing-animation-time;
      transition-timing-function: linear;

      padding: 10px 16px;

      &:hover {
        background-color: var(--frisbuy-color-primary-6);
      }

      &.frisbuy-select__option--disabled {
        pointer-events: none;
      }

      @media only screen and (max-width: 1024px) {
        padding: 12px 16px;
      }

      .frisbuy-select__option-text {
        color: var(--frisbuy-color-primary);
        font-size: 14px;
        line-height: 20px;
        font-weight: 400;
        white-space: nowrap;

        @media only screen and (max-width: 1024px) {
          line-height: 24px;
          font-size: 16px;
        }
      }

      &.frisbuy-select__option--selected {
        .frisbuy-select__option-text {
          font-weight: 700;
        }
      }

      .frisbuy-select__option-check {
        margin-left: 14px;
        flex-shrink: 0;
        color: var(--frisbuy-color-primary-9, #99cc33);
      }
    }
  }
}
</style>

<script setup lang="ts">
import { HTMLAttributes, onMounted, ref, watch } from "vue";
import vClickOutside from "@/common/directives/vClickOutside";

type TSelectOption = {
  value: string | number | symbol | undefined;
  sort?: "asc" | "desc";
  text: string;
};

const props = withDefaults(
  defineProps<{
    modelValue?: TSelectOption;

    options?: Array<TSelectOption>;
    defaultOption?: TSelectOption;
    customStyle?: HTMLAttributes["style"];
    disabled?: boolean;
  }>(),
  {
    modelValue: () => {
      return { value: undefined, text: "" };
    },
  }
);

const emit = defineEmits<{
  "update:modelValue": [option: TSelectOption];
}>();

onMounted(() => {
  if (props.defaultOption) {
    setDefaultOption(props.defaultOption);
  }
});

const selectOptions = ref();
const isSelectMenuOpen = ref(false);

function openSelectMenu() {
  if (props.disabled) {
    return;
  }

  isSelectMenuOpen.value = true;

  selectOptions?.value.focus();
}

function closeSelectMenu() {
  isSelectMenuOpen.value = false;
}

function selectMenuClickHandler() {
  if (props.disabled) {
    return;
  }

  if (isSelectMenuOpen.value) {
    closeSelectMenu();
  } else {
    /* NOTE:
      vClickOutside without this timeout is called immediately
      after openSelectMenu via select-button's @click
    */
    setTimeout(() => {
      openSelectMenu();
    });
  }
}

function setDefaultOption(option: TSelectOption) {
  selectedOption.value = option;
}

const selectedOption = ref<TSelectOption>(props.modelValue);
function selectOption(option: TSelectOption) {
  if (props.disabled) {
    return;
  }

  emit("update:modelValue", option);

  selectedOption.value = option;
  closeSelectMenu();
}

watch(
  () => props.modelValue,
  (value) => {
    selectedOption.value = value;
  }
);
</script>
